import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { MessageCircle, ArrowLeft, Star, Tag, Loader2, Bookmark, BookmarkCheck, Users, Share2, X, Pencil, Crown, Flag, Rocket, CreditCard, Send, Image, Trash2, Sparkles } from 'lucide-react';
import AnimatedAvatar from '../components/AnimatedAvatar';
import { ReportModal } from '../components/ReportModal';
import { GemIcon } from '../components/GemIcon';
import UltimateTag from '../components/UltimateTag';
import { fallbackAvatarUrl } from '../utils/fallbackAvatar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_NAME_COLORS = {
  free: 'text-white',
  premium: 'text-[#F59E0B]',
  ultimate: 'text-[#E11D48]',
};

const TIER_GLOW = {
  premium: '0 0 30px rgba(245,158,11,0.1)',
  ultimate: '0 0 30px rgba(225,29,72,0.1)',
};

export default function CharacterDetail() {
  const { id } = useParams();
  const { user, checkAuth: refreshUser } = useAuth();
  const navigate = useNavigate();
  const [character, setCharacter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [isCollected, setIsCollected] = useState(false);
  const [collectLoading, setCollectLoading] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [userRating, setUserRating] = useState(0);
  const [userComment, setUserComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [showShareToast, setShowShareToast] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [boosting, setBoosting] = useState(false);
  const [boostBuying, setBoostBuying] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [commentImage, setCommentImage] = useState('');
  const [postingComment, setPostingComment] = useState(false);
  const [uploadingCommentImg, setUploadingCommentImg] = useState(false);
  const commentImageRef = useRef(null);

  useEffect(() => {
    fetchCharacter();
    fetchReviews();
    fetchComments();
    if (user) checkCollected();
  }, [id, user]);

  const fetchComments = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/characters/${id}/comments`);
      setComments(data);
    } catch {}
  };

  const postComment = async () => {
    if (!commentText.trim() || postingComment) return;
    setPostingComment(true);
    try {
      const payload = { content: commentText.trim() };
      if (commentImage) payload.image_url = commentImage;
      const { data } = await axios.post(`${API_URL}/api/characters/${id}/comments`, payload, { withCredentials: true });
      setComments(prev => [data, ...prev]);
      setCommentText('');
      setCommentImage('');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to post comment');
    } finally {
      setPostingComment(false);
    }
  };

  const deleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await axios.delete(`${API_URL}/api/comments/${commentId}`, { withCredentials: true });
      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch {}
  };

  const uploadCommentImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    if (file.size > 5 * 1024 * 1024) { alert('Image must be under 5MB'); return; }
    setUploadingCommentImg(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API_URL}/api/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setCommentImage(data.url);
    } catch {
      alert('Failed to upload image');
    } finally {
      setUploadingCommentImg(false);
    }
  };

  const fetchCharacter = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/characters/${id}`, { withCredentials: true });
      setCharacter(data);
      // Update page title and meta for SEO/sharing
      document.title = `${data.name} - Chat on Animus`;
      const metaDesc = document.querySelector('meta[property="og:description"]');
      if (metaDesc) metaDesc.setAttribute('content', data.description?.slice(0, 200) || '');
      const metaTitle = document.querySelector('meta[property="og:title"]');
      if (metaTitle) metaTitle.setAttribute('content', `${data.name} - Chat on Animus`);
      const metaImg = document.querySelector('meta[property="og:image"]');
      if (metaImg && data.avatar_url) metaImg.setAttribute('content', data.avatar_url);
    } catch { navigate('/'); }
    finally { setLoading(false); }
  };

  const fetchReviews = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/characters/${id}/reviews`);
      setReviews(data);
    } catch (err) { console.error('Reviews error:', err); }
  };

  const checkCollected = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/characters/${id}/collected`, { withCredentials: true });
      setIsCollected(data.collected);
    } catch {}
  };

  const toggleCollect = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { navigate('/login', { state: { from: { pathname: `/character/${id}` } } }); return; }
    setCollectLoading(true);
    try {
      if (isCollected) {
        await axios.delete(`${API_URL}/api/characters/${id}/collect`, { withCredentials: true });
        setIsCollected(false);
      } else {
        await axios.post(`${API_URL}/api/characters/${id}/collect`, {}, { withCredentials: true });
        setIsCollected(true);
      }
    } catch {}
    finally { setCollectLoading(false); }
  };

  const startChat = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { navigate('/login', { state: { from: { pathname: `/character/${id}` } } }); return; }
    setStarting(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/conversations/${id}`, {}, { withCredentials: true });
      navigate(`/chat/${data.id}`);
    } catch { alert('Failed to start chat'); }
    finally { setStarting(false); }
  };

  const submitReview = async () => {
    if (!user || userRating === 0) return;
    setSubmittingReview(true);
    try {
      await axios.post(`${API_URL}/api/characters/${id}/reviews`, { rating: userRating, comment: userComment }, { withCredentials: true });
      setUserComment('');
      await fetchReviews();
      await fetchCharacter();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to submit review'); }
    finally { setSubmittingReview(false); }
  };

  const shareCharacter = async () => {
    const shareUrl = `${window.location.origin}/character/${id}`;
    const shareData = { title: character?.name, text: `Chat with ${character?.name} on Animus!`, url: shareUrl };
    
    if (navigator.share) {
      try { await navigator.share(shareData); } catch {}
    } else {
      await navigator.clipboard.writeText(shareUrl);
      setShowShareToast(true);
      setTimeout(() => setShowShareToast(false), 2000);
    }
  };

  const boostCharacter = async () => {
    if (!window.confirm('Boost this character for 2 hours? Costs 1,500 gems.')) return;
    setBoosting(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/characters/${id}/boost`, {}, { withCredentials: true });
      alert(data.message);
      await fetchCharacter();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to boost character');
    } finally {
      setBoosting(false);
    }
  };

  const boostWithStripe = async () => {
    setBoostBuying(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/characters/boost/purchase`, {
        character_id: id,
        origin_url: window.location.origin,
      }, { withCredentials: true });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setBoostBuying(false);
    }
  };

  const redeemBoostCredit = async () => {
    if (!window.confirm('Use 1 free boost credit to boost this character for 2 hours?')) return;
    setBoosting(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/characters/${id}/boost/redeem-credit`, {}, { withCredentials: true });
      alert(data.message);
      await fetchCharacter();
      if (refreshUser) await refreshUser();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to redeem credit');
    } finally {
      setBoosting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#E11D48] animate-spin" /></div>;
  if (!character) return null;

  const defaultAvatar = fallbackAvatarUrl(character.name);

  return (
    <div className="min-h-screen pb-40 lg:pb-12">
      <div className="max-w-5xl mx-auto">
      {/* Share Toast */}
      {showShareToast && (
        <div className="fixed top-16 left-1/2 -translate-x-1/2 z-50 px-4 py-2 bg-[#E11D48] text-white text-sm rounded-full shadow-lg">Link copied!</div>
      )}

      {/* Hero — Full bleed */}
      <div className="relative h-[45vh] overflow-hidden">
        <div className="absolute inset-0">
          <AnimatedAvatar
            src={character.avatar_url || defaultAvatar}
            alt={character.name}
            className="w-full h-full object-cover"
            rounded={false}
            intensity="lively"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-[#050505]/20" />
        <div className="absolute top-4 left-4 right-4 flex items-center justify-between" style={{ zIndex: 10 }}>
          <button onClick={() => navigate(-1)} className="p-3 bg-black/50 backdrop-blur-md rounded-2xl text-white active:scale-95" data-testid="back-button">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex gap-2">
            <button onClick={shareCharacter} className="p-3 bg-black/50 backdrop-blur-md rounded-2xl text-white active:scale-95" data-testid="share-button">
              <Share2 className="w-5 h-5" />
            </button>
            <button onClick={toggleCollect} disabled={collectLoading} className={`p-3 backdrop-blur-md rounded-2xl active:scale-95 ${isCollected ? 'bg-[#E11D48]/80 text-white' : 'bg-black/50 text-white'}`} data-testid="collect-button">
              {collectLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : isCollected ? <BookmarkCheck className="w-5 h-5" /> : <Bookmark className="w-5 h-5" />}
            </button>
            {user && character && user.id !== character.creator_id && (
              <button onClick={() => setShowReport(true)} className="p-3 bg-black/50 backdrop-blur-md rounded-2xl text-white active:scale-95" data-testid="report-character-btn">
                <Flag className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        {/* Bottom overlay with name */}
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <h1
            className={`text-2xl font-bold font-['Unbounded'] mb-1 ${TIER_NAME_COLORS[character.creator_tier] || 'text-white'}`}
            style={{ textShadow: '0 2px 12px rgba(0,0,0,0.8)' }}
            data-testid="character-name"
          >
            {character.name}
            {character.is_boosted && (
              <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase bg-purple-500/30 text-purple-300 border border-purple-500/30 rounded-full align-middle backdrop-blur-sm">
                <Rocket className="w-3 h-3" /> Boosted
              </span>
            )}
          </h1>
          <button onClick={() => navigate(`/user/${character.creator_id}`)} className="flex items-center gap-1.5 text-sm text-white/70 hover:text-white transition-colors flex-wrap" data-testid="character-creator-link">
            <Users className="w-3.5 h-3.5" /> {character.creator_name}
            {character.creator_ultimate_tag && <UltimateTag tag={character.creator_ultimate_tag} size="xs" />}
            {character.creator_badge && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider bg-gradient-to-r from-[#F59E0B] to-[#E11D48] text-white rounded-full" data-testid="creator-badge">
                <Crown className="w-2 h-2" /> {character.creator_badge}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="relative px-4 pt-4">
        {/* Stats Row */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 p-3 bg-[#111318] rounded-2xl text-center border border-white/[0.04]">
            <MessageCircle className="w-4 h-4 text-[#E11D48] mx-auto mb-1" />
            <p className="text-white font-bold text-sm">{(character.chat_count || 0).toLocaleString()}</p>
            <p className="text-[9px] text-[#71717A]">Chats</p>
          </div>
          <div className="flex-1 p-3 bg-[#111318] rounded-2xl text-center border border-white/[0.04]">
            <Star className="w-4 h-4 text-[#F59E0B] mx-auto mb-1" />
            <p className="text-white font-bold text-sm">{character.avg_rating || '—'}</p>
            <p className="text-[9px] text-[#71717A]">{character.review_count || 0} Reviews</p>
          </div>
          <div className="flex-1 p-3 bg-[#111318] rounded-2xl text-center border border-white/[0.04]">
            <Bookmark className="w-4 h-4 text-[#A855F7] mx-auto mb-1" />
            <p className="text-white font-bold text-sm">{character.collection_count || 0}</p>
            <p className="text-[9px] text-[#71717A]">Saves</p>
          </div>
        </div>
        
        {/* Creator Actions */}
        {user && character.creator_id === user.id && (
          <div className="mb-4">
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={() => navigate(`/character/${id}/edit`)} className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium bg-[#111318] border border-white/[0.06] rounded-xl text-[#A1A1AA] hover:text-white transition-colors" data-testid="edit-character-btn">
                <Pencil className="w-3.5 h-3.5" /> Edit
              </button>
              {!character.is_boosted && (
                <>
                  {(user.boost_credits || 0) > 0 && (
                    <button onClick={redeemBoostCredit} disabled={boosting} className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold bg-gradient-to-r from-[#F59E0B] to-[#E11D48] rounded-xl text-white shadow-lg shadow-[#E11D48]/20 hover:opacity-90 transition disabled:opacity-50" data-testid="redeem-boost-credit-btn">
                      {boosting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                      Free Boost ({user.boost_credits})
                    </button>
                  )}
                  <button onClick={boostCharacter} disabled={boosting} className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium bg-purple-500/10 border border-purple-500/20 rounded-xl text-purple-400 hover:bg-purple-500/20 transition-colors disabled:opacity-50" data-testid="boost-character-btn">
                    {boosting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <GemIcon size={12} />}
                    1,500 Gems
                  </button>
                  <button onClick={boostWithStripe} disabled={boostBuying} className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium bg-[#E11D48]/10 border border-[#E11D48]/20 rounded-xl text-[#E11D48] hover:bg-[#E11D48]/20 transition-colors disabled:opacity-50" data-testid="boost-purchase-btn">
                    {boostBuying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CreditCard className="w-3.5 h-3.5" />}
                    £1.99
                  </button>
                </>
              )}
            </div>

            {/* Boost Streak Progress */}
            {!character.is_boosted && user.monthly_boost && !user.monthly_boost.credit_awarded && (
              <div className="mt-3 p-3 bg-gradient-to-r from-[#F59E0B]/10 to-[#E11D48]/10 border border-[#F59E0B]/20 rounded-xl" data-testid="boost-streak-progress">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[11px] font-bold text-[#F59E0B] uppercase tracking-wider flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3" /> Boost Streak
                  </p>
                  <p className="text-[10px] text-[#A1A1AA]">{(user.monthly_boost.char_ids?.length || 0)}/3 this month</p>
                </div>
                <div className="flex gap-1 mb-1.5">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className={`flex-1 h-1.5 rounded-full ${i < (user.monthly_boost.char_ids?.length || 0) ? 'bg-gradient-to-r from-[#F59E0B] to-[#E11D48]' : 'bg-white/10'}`} />
                  ))}
                </div>
                <p className="text-[10px] text-[#A1A1AA]">Boost 3 different characters this month → unlock 1 free boost</p>
              </div>
            )}
          </div>
        )}

        {/* Description */}
        <p className="text-[#D4D4D8] mb-4 text-sm leading-relaxed" data-testid="character-description">{character.description}</p>

        {/* Tags */}
        {character.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {character.tags.map((tag, i) => (
              <span key={i} className="px-2.5 py-1 text-[11px] bg-white/[0.04] border border-white/[0.06] rounded-full text-[#A1A1AA]">
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Greeting Preview */}
        <div className="p-4 bg-[#111318] rounded-2xl border border-white/[0.04] mb-4">
          <p className="text-[10px] text-[#555] uppercase tracking-wider font-medium mb-2">First Message</p>
          <p className="text-[#A1A1AA] italic text-sm leading-relaxed" data-testid="character-greeting">"{character.greeting}"</p>
        </div>

        {/* Rate & Review */}
        {user && (
          <div className="p-4 bg-[#0F1115] rounded-xl border border-white/5 mb-4">
            <p className="text-xs text-[#71717A] uppercase tracking-wider mb-2">Rate this character</p>
            <div className="flex items-center gap-1 mb-3">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setUserRating(star)}
                  className="p-0.5"
                  data-testid={`star-${star}`}
                >
                  <Star className={`w-6 h-6 transition-colors ${star <= userRating ? 'text-[#F59E0B] fill-[#F59E0B]' : 'text-[#71717A]'}`} />
                </button>
              ))}
            </div>
            {userRating > 0 && (
              <>
                <Textarea
                  value={userComment}
                  onChange={(e) => setUserComment(e.target.value.slice(0, 500))}
                  placeholder="Write a short review (optional)"
                  rows={2}
                  className="bg-[#1A1D24] border-white/10 text-white placeholder:text-[#71717A] rounded-lg text-sm mb-2 resize-none"
                  data-testid="review-comment"
                />
                <Button
                  onClick={submitReview}
                  disabled={submittingReview}
                  size="sm"
                  className="bg-[#E11D48] hover:bg-[#BE123C] text-white rounded-full text-xs"
                  data-testid="submit-review-btn"
                >
                  {submittingReview ? 'Saving...' : 'Submit Review'}
                </Button>
              </>
            )}
          </div>
        )}

        {/* Reviews */}
        {reviews.length > 0 && (
          <div className="mb-4">
            <p className="text-xs text-[#71717A] uppercase tracking-wider mb-2">Reviews</p>
            <div className="space-y-2">
              {reviews.slice(0, 5).map((review, i) => (
                <div key={i} className="p-3 bg-[#0F1115] rounded-lg border border-white/5">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs text-white font-medium">{review.user_name}</span>
                    <div className="flex">
                      {[...Array(5)].map((_, s) => (
                        <Star key={s} className={`w-3 h-3 ${s < review.rating ? 'text-[#F59E0B] fill-[#F59E0B]' : 'text-[#71717A]'}`} />
                      ))}
                    </div>
                  </div>
                  {review.comment && <p className="text-xs text-[#A1A1AA]">{review.comment}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Comments */}
        <div className="mb-4" data-testid="comments-section">
          <p className="text-xs text-[#71717A] uppercase tracking-wider mb-2">Comments {comments.length > 0 && `· ${comments.length}`}</p>

          {/* Composer */}
          {user && (
            <div className="p-3 bg-[#0F1115] rounded-xl border border-white/5 mb-3">
              <Textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value.slice(0, 500))}
                placeholder="Leave a comment..."
                rows={2}
                className="bg-[#1A1D24] border-white/10 text-white placeholder:text-[#71717A] rounded-lg text-sm mb-2 resize-none"
                data-testid="comment-input"
              />
              {commentImage && (
                <div className="relative inline-block mb-2">
                  <img src={commentImage} alt="attachment" className="max-h-32 rounded-lg border border-white/10" />
                  <button
                    type="button"
                    onClick={() => setCommentImage('')}
                    className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-black/80 border border-white/10 flex items-center justify-center"
                    data-testid="remove-comment-image-btn"
                  >
                    <X className="w-3.5 h-3.5 text-white" />
                  </button>
                </div>
              )}
              <div className="flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={() => commentImageRef.current?.click()}
                  disabled={uploadingCommentImg}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-[#A1A1AA] bg-white/5 hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50"
                  data-testid="comment-image-btn"
                >
                  {uploadingCommentImg ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Image className="w-3.5 h-3.5" />}
                  Photo
                </button>
                <input ref={commentImageRef} type="file" accept="image/*" onChange={uploadCommentImage} className="hidden" />
                <Button
                  onClick={postComment}
                  disabled={!commentText.trim() || postingComment}
                  size="sm"
                  className="bg-[#E11D48] hover:bg-[#BE123C] text-white rounded-full text-xs flex items-center gap-1.5"
                  data-testid="submit-comment-btn"
                >
                  {postingComment ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  Post
                </Button>
              </div>
            </div>
          )}

          {/* List */}
          {comments.length === 0 ? (
            <p className="text-xs text-[#71717A] text-center py-4">No comments yet. Be the first!</p>
          ) : (
            <div className="space-y-2">
              {comments.map((c) => {
                const isOwner = user && (c.user_id === user.id || character.creator_id === user.id);
                return (
                  <div key={c.id} className="p-3 bg-[#0F1115] rounded-xl border border-white/5" data-testid={`comment-${c.id}`}>
                    <div className="flex items-start gap-2.5">
                      {c.user_avatar ? (
                        <img src={c.user_avatar} alt={c.user_name} className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0">
                          <Users className="w-4 h-4 text-[#71717A]" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <button
                            onClick={() => c.user_id && navigate(`/user/${c.user_id}`)}
                            className="text-xs font-medium text-white hover:text-[#E11D48] transition-colors truncate"
                          >
                            {c.user_name || 'User'}
                          </button>
                          {isOwner && (
                            <button
                              onClick={() => deleteComment(c.id)}
                              className="text-[#71717A] hover:text-[#E11D48] transition-colors flex-shrink-0"
                              data-testid={`delete-comment-${c.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                        {c.content && <p className="text-xs text-[#D4D4D8] leading-relaxed whitespace-pre-wrap break-words">{c.content}</p>}
                        {c.image_url && (
                          <img src={c.image_url} alt="comment" className="mt-2 max-h-48 rounded-lg border border-white/10" />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Start Chat */}
        <div className="mt-4 mb-32 px-4">
          <button onClick={startChat} onTouchEnd={(e) => { e.preventDefault(); startChat(e); }} disabled={starting} className="w-full flex items-center justify-center gap-2 bg-[#E11D48] hover:bg-[#BE123C] active:bg-[#9F1239] text-white py-5 rounded-2xl text-lg font-bold shadow-lg shadow-[#E11D48]/30 transition-all active:scale-[0.98] touch-manipulation disabled:opacity-50" data-testid="start-chat-button" style={{ zIndex: 9999, pointerEvents: 'auto' }}>
            {starting ? <><Loader2 className="w-6 h-6 animate-spin" /><span>Starting...</span></> : <><MessageCircle className="w-6 h-6" /><span>Start Chatting</span></>}
          </button>
        </div>
      </div>

      {/* Report Modal */}
      {character && (
        <ReportModal
          isOpen={showReport}
          onClose={() => setShowReport(false)}
          targetType="character"
          targetId={character.id}
          targetName={character.name}
        />
      )}
      </div>
    </div>
  );
}
